
import { defineComponent, nextTick } from "vue";

export default defineComponent({
  name: "otp-input",
  props: {
    modelValue: { type: String, default: "" },
    length: { type: Number, default: 6 },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "complete"],
  data() {
    return {
      digits: [] as string[]
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val: string) {
        this.syncFromValue(val);
      }
    },
    length() {
      this.syncFromValue(this.modelValue);
    }
  },
  methods: {
    syncFromValue(val: string) {
      const chars = String(val || "")
        .replace(/\D/g, "")
        .slice(0, this.length)
        .split("");
      const next: string[] = [];
      for (let i = 0; i < this.length; i++) {
        next.push(chars[i] || "");
      }
      this.digits = next;
    },
    emitValue() {
      const value = this.digits.join("");
      this.$emit("update:modelValue", value);
      if (value.length === this.length) {
        this.$emit("complete", value);
      }
    },
    boxAt(index: number): HTMLInputElement | null {
      const root = this.$refs.root as HTMLElement | undefined;
      if (!root) return null;
      const inputs = root.querySelectorAll("input");
      return (inputs[index] as HTMLInputElement) || null;
    },
    focusBox(index: number) {
      nextTick(() => {
        const box = this.boxAt(index);
        if (box) {
          box.focus();
          box.select();
        }
      });
    },
    onInput(index: number, event: Event) {
      const target = event.target as HTMLInputElement;
      const clean = target.value.replace(/\D/g, "");
      const char = clean ? clean[clean.length - 1] : "";
      this.digits.splice(index, 1, char);
      target.value = char;
      this.emitValue();
      if (char && index < this.length - 1) {
        this.focusBox(index + 1);
      }
    },
    onKeydown(index: number, event: KeyboardEvent) {
      if (event.key === "Backspace") {
        if (this.digits[index]) {
          this.digits.splice(index, 1, "");
          this.emitValue();
        } else if (index > 0) {
          this.digits.splice(index - 1, 1, "");
          this.emitValue();
          this.focusBox(index - 1);
        }
        event.preventDefault();
      } else if (event.key === "ArrowLeft" && index > 0) {
        this.focusBox(index - 1);
        event.preventDefault();
      } else if (event.key === "ArrowRight" && index < this.length - 1) {
        this.focusBox(index + 1);
        event.preventDefault();
      }
    },
    onPaste(event: ClipboardEvent) {
      event.preventDefault();
      const text = (event.clipboardData?.getData("text") || "")
        .replace(/\D/g, "")
        .slice(0, this.length);
      if (!text) {
        return;
      }
      const chars = text.split("");
      const next: string[] = [];
      for (let i = 0; i < this.length; i++) {
        next.push(chars[i] || "");
      }
      this.digits = next;
      this.emitValue();
      const focusIndex = Math.min(text.length, this.length - 1);
      this.focusBox(focusIndex);
    },
    selectOnFocus(event: FocusEvent) {
      (event.target as HTMLInputElement).select();
    }
  }
});
